// @ts-strict-ignore
import { Matrix4, Vector3 } from 'three';
import { MathUtils } from '../common/util/math-utils';

export class Rectangle3D {
  /**
   * Top-left point
   */
  public point1: Vector3;
  /**
   * Top-right point
   */
  public point2: Vector3;
  /**
   * Bottom-right point
   */
  public point3: Vector3;
  /**
   * Bottom-left point
   */
  public point4: Vector3;

  /**
		 * Create rectangle object by 4 points
		 *
		 * @param points array of points
		 * @return rectangle
		 *
		 */
  public static fromArray(points: Vector3[]): Rectangle3D {
    const rect: Rectangle3D = new Rectangle3D();
    rect.point1 = points[0];
    rect.point2 = points[1];
    rect.point3 = points[2];
    rect.point4 = points[3];
    return rect;
  }

  /**
   * Rectangle width - distance between first and fourth point (in flex: first and second point).
   * @return width
   */
  public get width(): number {
    if (this.point1 && this.point4) {
      return this.point1.distanceTo(this.point4);
    }
    return null;
  }

  /**
   * Rectangle height - distance between first and second point (in flex: first and fourth point).
   * @return height
   */
  public get height(): number {
    if (this.point1 && this.point2) {
      return this.point1.distanceTo(this.point2);
    }
    return null;
  }

  /**
   * Provides face of the rectangle (between point1, point2 and point4)
   * @return array of points, defining the face
   */
  public toFace(): Vector3[] {
    return [this.point1, this.point2, this.point4];
  }

  /**
   * @return array of all rectangle points
   *
   */
  public toArray(): Vector3[] {
    return [this.point1, this.point2, this.point3, this.point4];
  }

  public clone(): Rectangle3D {
    const clonedRectangle: Rectangle3D = new Rectangle3D();
    clonedRectangle.point1 = this.point1.clone();
    clonedRectangle.point2 = this.point2.clone();
    clonedRectangle.point3 = this.point3.clone();
    clonedRectangle.point4 = this.point4.clone();
    return clonedRectangle;
  }

  /**
   * Transforms all rectangle points by some matrix (rotate and move).
   * Note: scale is not applied considered
   *
   * @param matrix transform matrix
   */
  public transform(matrix: Matrix4): this {
    let points: Vector3[] = this.toArray();
    points = MathUtils.transformVectors(points, matrix);
    this.point1 = points[0];
    this.point2 = points[1];
    this.point3 = points[2];
    this.point4 = points[3];
    return this;
  }

  public flipHorizontal(): this {
    // swap points along the "width" direction.
    // point1 <--> point4 and point2 <--> point3
    let temp: Vector3;

    temp = this.point1;
    this.point1 = this.point4;
    this.point4 = temp;

    temp = this.point2;
    this.point2 = this.point3;
    this.point3 = temp;

    return this;
  }

  public flipVertical(): this {
    // swap points along the "height" direction.
    // point1 <--> point2 and point3 <--> point4
    let temp: Vector3;

    temp = this.point1;
    this.point1 = this.point2;
    this.point2 = temp;

    temp = this.point3;
    this.point3 = this.point4;
    this.point4 = temp;

    return this;
  }

  public get center(): Vector3 {
    return new Vector3(
      (this.point1.x + this.point2.x + this.point3.x + this.point4.x) / 4,
      (this.point1.y + this.point2.y + this.point3.y + this.point4.y) / 4,
      (this.point1.z + this.point2.z + this.point3.z + this.point4.z) / 4
    );
  }
}
